/**
 * Profile Page - Regular Users
 * Basic profile management and password changes
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Lock, Save, Loader2, AlertCircle, CheckCircle, Calendar, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';
import { validateEmail, validatePassword, validateName } from '../utils/validation';
import { updateUserProfile, changePassword } from '../services/userService';
import { getErrorMessage } from '../services/apiService';
import { setUserData } from '../utils/tokenManager';

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  // Profile form
  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
  });
  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({});
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);

  // Password form
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
      });
    }
  }, [user]);

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setProfileErrors({});
    setProfileSuccess(false);

    // Validation
    const errors: Record<string, string> = {};

    const firstNameVal = validateName(profileData.firstName, 'First name');
    if (!firstNameVal.isValid) errors.firstName = firstNameVal.error!;

    const lastNameVal = validateName(profileData.lastName, 'Last name');
    if (!lastNameVal.isValid) errors.lastName = lastNameVal.error!;

    const emailVal = validateEmail(profileData.email);
    if (!emailVal.isValid) errors.email = emailVal.error!;

    if (Object.keys(errors).length > 0) {
      setProfileErrors(errors);
      return;
    }

    setIsSavingProfile(true);

    try {
      const updatedUser = await updateUserProfile(profileData);
      setUserData(updatedUser);
      updateUser(updatedUser);
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setPasswordErrors({});
    setPasswordSuccess(false);

    // Validation
    const errors: Record<string, string> = {};

    if (!passwordData.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }

    const newPasswordVal = validatePassword(passwordData.newPassword);
    if (!newPasswordVal.isValid) errors.newPassword = newPasswordVal.error!;

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }

    setIsSavingPassword(true);

    try {
      await changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword,
      });
      setPasswordSuccess(true);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSavingPassword(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] text-[#0f172a]">
      {/* Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-radial from-[#c41e3a]/[0.03] to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-radial from-[#c41e3a]/[0.04] to-transparent rounded-full blur-3xl"></div>
      </div>

      <Navigation />

      {/* Header */}
      <div className="relative z-20 px-4 md:px-8 pt-28 md:pt-32 pb-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center justify-center w-10 h-10 rounded-xl border border-black/[0.08] bg-white/60 transition-all hover:bg-white/80"
              aria-label="Back to home"
            >
              <ArrowLeft className="w-5 h-5 text-[#0f172a]" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-[#0f172a]">Profile Settings</h1>
              <p className="text-[#64748b] mt-1">Manage your account information</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 px-4 md:px-8 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Global Error */}
          {error && (
            <div className="p-4 rounded-xl border border-[#dc2626]/30 flex items-start space-x-3 bg-[#dc2626]/10">
              <AlertCircle className="w-5 h-5 text-[#dc2626] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#dc2626]">{error}</p>
            </div>
          )}

          {/* Profile Information */}
          <div className="glass-card rounded-3xl p-6 border border-black/[0.08]">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#c41e3a]/10">
                <User className="w-6 h-6 text-[#c41e3a]" />
              </div>
              <h2 className="text-xl font-semibold text-[#0f172a]">Profile Information</h2>
            </div>

            {profileSuccess && (
              <div className="mb-4 p-3 rounded-xl border border-[#16a34a]/30 flex items-center space-x-2 bg-[#16a34a]/10">
                <CheckCircle className="w-5 h-5 text-[#16a34a]" />
                <p className="text-sm text-[#16a34a]">Profile updated successfully!</p>
              </div>
            )}

            <form onSubmit={handleProfileSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#475569] mb-2">First Name</label>
                  <input
                    type="text"
                    value={profileData.firstName}
                    onChange={(e) => {
                      setProfileData({ ...profileData, firstName: e.target.value });
                      setProfileErrors({ ...profileErrors, firstName: '' });
                    }}
                    className={`w-full px-4 py-3 rounded-xl border bg-white/80 text-[#0f172a] placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#c41e3a] transition-all ${
                      profileErrors.firstName ? 'border-[#dc2626]' : 'border-black/[0.08]'
                    }`}
                  />
                  {profileErrors.firstName && (
                    <p className="mt-2 text-sm text-[#dc2626]">{profileErrors.firstName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#475569] mb-2">Last Name</label>
                  <input
                    type="text"
                    value={profileData.lastName}
                    onChange={(e) => {
                      setProfileData({ ...profileData, lastName: e.target.value });
                      setProfileErrors({ ...profileErrors, lastName: '' });
                    }}
                    className={`w-full px-4 py-3 rounded-xl border bg-white/80 text-[#0f172a] placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#c41e3a] transition-all ${
                      profileErrors.lastName ? 'border-[#dc2626]' : 'border-black/[0.08]'
                    }`}
                  />
                  {profileErrors.lastName && (
                    <p className="mt-2 text-sm text-[#dc2626]">{profileErrors.lastName}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#475569] mb-2">Email Address</label>
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => {
                    setProfileData({ ...profileData, email: e.target.value });
                    setProfileErrors({ ...profileErrors, email: '' });
                  }}
                  className={`w-full px-4 py-3 rounded-xl border bg-white/80 text-[#0f172a] placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#c41e3a] transition-all ${
                    profileErrors.email ? 'border-[#dc2626]' : 'border-black/[0.08]'
                  }`}
                />
                {profileErrors.email && (
                  <p className="mt-2 text-sm text-[#dc2626]">{profileErrors.email}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSavingProfile}
                className="btn-primary disabled:opacity-50"
              >
                {isSavingProfile ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Password Management */}
          <div className="glass-card rounded-3xl p-6 border border-black/[0.08]">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#c41e3a]/10">
                <Lock className="w-6 h-6 text-[#c41e3a]" />
              </div>
              <h2 className="text-xl font-semibold text-[#0f172a]">Change Password</h2>
            </div>

            {passwordSuccess && (
              <div className="mb-4 p-3 rounded-xl border border-[#16a34a]/30 flex items-center space-x-2 bg-[#16a34a]/10">
                <CheckCircle className="w-5 h-5 text-[#16a34a]" />
                <p className="text-sm text-[#16a34a]">Password changed successfully!</p>
              </div>
            )}

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#475569] mb-2">Current Password</label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => {
                    setPasswordData({ ...passwordData, currentPassword: e.target.value });
                    setPasswordErrors({ ...passwordErrors, currentPassword: '' });
                  }}
                  className={`w-full px-4 py-3 rounded-xl border bg-white/80 text-[#0f172a] placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#c41e3a] transition-all ${
                    passwordErrors.currentPassword ? 'border-[#dc2626]' : 'border-black/[0.08]'
                  }`}
                />
                {passwordErrors.currentPassword && (
                  <p className="mt-2 text-sm text-[#dc2626]">{passwordErrors.currentPassword}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#475569] mb-2">New Password</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => {
                    setPasswordData({ ...passwordData, newPassword: e.target.value });
                    setPasswordErrors({ ...passwordErrors, newPassword: '' });
                  }}
                  className={`w-full px-4 py-3 rounded-xl border bg-white/80 text-[#0f172a] placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#c41e3a] transition-all ${
                    passwordErrors.newPassword ? 'border-[#dc2626]' : 'border-black/[0.08]'
                  }`}
                />
                {passwordErrors.newPassword && (
                  <p className="mt-2 text-sm text-[#dc2626]">{passwordErrors.newPassword}</p>
                )}
                <PasswordStrengthIndicator password={passwordData.newPassword} />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#475569] mb-2">Confirm New Password</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => {
                    setPasswordData({ ...passwordData, confirmPassword: e.target.value });
                    setPasswordErrors({ ...passwordErrors, confirmPassword: '' });
                  }}
                  className={`w-full px-4 py-3 rounded-xl border bg-white/80 text-[#0f172a] placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#c41e3a] transition-all ${
                    passwordErrors.confirmPassword ? 'border-[#dc2626]' : 'border-black/[0.08]'
                  }`}
                />
                {passwordErrors.confirmPassword && (
                  <p className="mt-2 text-sm text-[#dc2626]">{passwordErrors.confirmPassword}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSavingPassword}
                className="btn-primary disabled:opacity-50"
              >
                {isSavingPassword ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Changing Password...</span>
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    <span>Change Password</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Account Information */}
          {user && (
            <div className="glass-card rounded-3xl p-6 border border-black/[0.08]">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#c41e3a]/10">
                  <Calendar className="w-6 h-6 text-[#c41e3a]" />
                </div>
                <h2 className="text-xl font-semibold text-[#0f172a]">Account Information</h2>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-black/[0.08]">
                  <span className="text-[#64748b]">Account Created</span>
                  <span className="text-[#0f172a] font-medium">{formatDate(user.createdAt)}</span>
                </div>
                {user.lastLoginAt && (
                  <div className="flex justify-between py-2 border-b border-black/[0.08]">
                    <span className="text-[#64748b]">Last Login</span>
                    <span className="text-[#0f172a] font-medium">{formatDate(user.lastLoginAt)}</span>
                  </div>
                )}
                <div className="flex justify-between py-2">
                  <span className="text-[#64748b]">User ID</span>
                  <span className="text-[#0f172a] font-mono text-xs">{user.id}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Profile;
